import { ArrowDown, ArrowUp } from 'lucide-react';

import { PremiumBadge } from '~/components/common/premium-badge';
import { Card, CardContent } from '~/components/ui/card';
import type { CatalogStatCardProps as BaseCatalogStatCardProps } from '~/features/catalog-operations/types/catalog-operations.types';
import { cn } from '~/utils/cn';

type CatalogStatCardProps = BaseCatalogStatCardProps & {
  /** Automation hook: `data-testid` for the card; the value and trend get `-value` / `-trend`. */
  testId?: string;
};

/**
 * Stat card used in the Catalog Operations dashboard.
 * Displays a single operational metric with an optional directional trend label.
 * When `onClick` is provided the card becomes interactive (focusable button-like).
 * When `isPremium` is true, a crown icon is rendered next to the label.
 */
export function CatalogStatCard({
  label,
  value,
  trend,
  onClick,
  isPremium,
  testId,
}: CatalogStatCardProps) {
  const interactive = typeof onClick === 'function';

  return (
    <Card
      data-testid={testId}
      className={cn(
        'flex-1 transition-shadow',
        interactive &&
          'focus-visible:ring-ring/50 cursor-pointer hover:shadow-md focus-visible:ring-2 focus-visible:outline-none',
      )}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-label={interactive ? `${label}: ${value}` : undefined}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
    >
      <CardContent className="pt-6">
        <p
          data-testid={testId ? `${testId}-label` : undefined}
          className="text-muted-foreground inline-flex items-center gap-1.5 text-sm"
        >
          {label}
          {isPremium && <PremiumBadge size={14} />}
        </p>
        <p
          data-testid={testId ? `${testId}-value` : undefined}
          className="mt-1 text-3xl font-bold tracking-tight"
        >
          {value}
        </p>

        {trend && (
          <div
            data-testid={testId ? `${testId}-trend` : undefined}
            className={cn(
              'mt-2 flex items-center gap-1 text-sm font-medium',
              trend.positive ? 'text-success' : 'text-error',
            )}
          >
            {trend.direction === 'up' && (
              <ArrowUp className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            )}
            {trend.direction === 'down' && (
              <ArrowDown className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            )}
            <span>{trend.label}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
